import { useContext } from "react";
import { GlobalContext } from "./GlobalContext";
import { useToast } from "./ToastContext";
import { apiFetch } from './api.js';

//Saving a sheet's column order/visibility — shared by CommonThead (desktop
//drag/context-menu) and the mobile sheet toolbar's columns dialog, so both
//write the preference the same way.
export default function useColumnPrefs(parentSheetName, setFieldOrder) {
    const { DealershipID, UserID, updateFieldOrder, fieldOrder: globalFieldOrder } = useContext(GlobalContext);
    const { showToast } = useToast();

    const persistFieldOrder = (fieldArr) => {
        //Capture the current context value before mirroring so we can roll it
        //back if the POST fails (prevents hasSavedPref from flipping permanently
        //while the DB has no record of the pref).
        const previousContextValue = globalFieldOrder?.[parentSheetName];

        //Mirror every persisted change into GlobalContext immediately so a
        //curated-default (pref-less) deal sheet flips to "has saved pref" the
        //moment the user hides/reorders anything — otherwise the wholesale
        //rebuild in useFieldOrder could clobber their change on the next refetch.
        updateFieldOrder(parentSheetName, fieldArr);
        apiFetch("/edituserpreferences", { body: {
                DealershipID: DealershipID,
                UserID: UserID,
                ownSheetName: parentSheetName,
                FieldOrder: fieldArr
            } }).then(async (res) => {
            const result = await res.json().catch(() => ({ message: res.statusText }));
            if (!res.ok) {
                //Roll back the context mirror so hasSavedPref stays coherent with the DB.
                updateFieldOrder(parentSheetName, previousContextValue);
                showToast('error', `Couldn't save column layout: ${result.message}`);
            }
        }).catch((err) => {
            //Network failure — same rollback.
            updateFieldOrder(parentSheetName, previousContextValue);
            showToast('error', `Couldn't save column layout: ${err.message}`);
        });
    };

    const applyVisibilityUpdate = (updatedFieldOrder) => {
        //The dialog only knows the visible columns (hidden cost columns are
        //filtered out upstream). Reconcile its reordered/toggled list back into
        //the full stored order: substitute the visible slots in dialog order
        //while leaving hidden cost columns exactly where they are, so they
        //aren't dropped from the saved preference.
        setFieldOrder(prev => {
            const updatedNames = new Set(updatedFieldOrder.map(f => f.name));
            let vi = 0;
            const merged = prev.map(f =>
                updatedNames.has(f.name) ? updatedFieldOrder[vi++] : f
            );
            //Fallback: if nothing was filtered, merged === updatedFieldOrder order.
            //persistFieldOrder now mirrors into GlobalContext itself.
            persistFieldOrder(merged);
            return merged;
        });
    };

    return { persistFieldOrder, applyVisibilityUpdate };
}
